// ContentTiles.ts
// TypeScript models and data loader for Groups, Topics, Subtopics

export interface Subtopic {
    id: string;
    title: string;
    parentTopicId: string;
}

export interface Topic {
    id: string;
    title: string;
    parentGroupId: string;
    subtopics: Subtopic[];
}

export interface Group {
    id: string;
    title: string;
    topics: Topic[];
}

export interface ContentTree {
    groups: Group[];
    topics: Topic[];
    subtopics: Subtopic[];
}

import { fetchContent } from '../services/contentService';

var globalTree = {};


// Fetch initial groups (for fast first render)
export async function fetchGroups(language: string): Promise<Group[]> {
    if (_StaticContentTree && _StaticContentTree['groups']) {
        // If groups are already fetched, return them
        return _StaticContentTree['groups'];
    }

    const query = `{ listGroups { data { id title orderNumber} } }`;
    const variables = { language };
    const response = await fetchContent(query, variables);
    // order the results by orderNumber
    response.listGroups.data.sort((a: any, b: any) => a.orderNumber - b.orderNumber);

    var groups = response.listGroups.data.map((g: any) => ({ ...g, topics: [] }));

    // create a deep copy clone of groups, for debugging purposes
    var groupsClone = JSON.parse(JSON.stringify(groups));
    globalTree['groups'] = groupsClone;

    return groups;
}

// Fetch all topics and subtopics in one go, and build the tree
export async function fetchAllTopicsAndSubtopics(language: string): Promise<{ topics: Topic[]; subtopics: Subtopic[] }> {
    let topics: Topic[] = [];
    let subtopics: Subtopic[] = [];

    if (_StaticContentTree && _StaticContentTree['topics']) {
        // If groups are already fetched, return them
        topics = _StaticContentTree['topics'];
    } else {
        // TODO: Need to combine this into a single call to fetchContent

        // Fetch all topics
        // TODO: add pagination support to dynamically fetch all topics if more than 150
        const topicsQuery = `{ listTopics  ( limit: 150) 
                                { data { id title parentGroup { id } orderNumber } } }`;
        const topicsResponse = await fetchContent(topicsQuery, { language });
        // order the results by orderNumber
        topicsResponse.listTopics.data.sort((a: any, b: any) => a.orderNumber - b.orderNumber);
        topics = topicsResponse.listTopics.data.map((t: any) => ({
            id: t.id,
            title: t.title,
            orderNumber: t.orderNumber,
            parentGroupId: t.parentGroup?.id || '',
            subtopics: [],
        }));

    }



    
    // create/store a deep copy clone of topics, for debugging purposes
    globalTree['topics'] = JSON.parse(JSON.stringify(topics)); // clone to avoid circular references

    //globalTree['topics'] = JSON.stringify(topics);

    if (_StaticContentTree && _StaticContentTree['subtopics']) {
        // If groups are already fetched, return them
        subtopics = _StaticContentTree['subtopics'];
    } else {
        // Fetch all subtopics
        // TODO: add pagination support to dynamically fetch all subtopics if more than 300
        const subtopicsQuery = `{ listSubtopics ( limit: 300)
                                    { data { id title parentTopic { id } orderNumber } } }`;
        const subtopicsResponse = await fetchContent(subtopicsQuery, { language });
        // order the results by orderBy
        subtopicsResponse.listSubtopics.data.sort((a: any, b: any) => a.orderNumber - b.orderNumber);
        subtopics = subtopicsResponse.listSubtopics.data.map((s: any) => {
            // Find the parent topic to get its parentGroupId
            let parentTopic = topics.find((t: Topic) => t.id === s.parentTopic?.id);
            return {
                id: s.id,
                title: s.title,
                orderNumber: s.orderNumber,
                parentTopicId: s.parentTopic?.id || '',
                parentGroupId: parentTopic ? parentTopic.parentGroupId : '',
            };
        });
    }

// create/store a deep copy clone of subtopics, for debugging purposes
    globalTree['subtopics'] = JSON.parse(JSON.stringify(subtopics));

    // Attach subtopics to topics
    topics.forEach(topic => {
        topic.subtopics = subtopics.filter(s => s.parentTopicId === topic.id);
    });

    return { topics, subtopics };
}

var _contentTree;
let orphans: { topics: Topic[]; subtopics: Subtopic[] } = {
    topics: [],
    subtopics: []
};

let noChildren: { groups: Group[]; topics: Topic[] } = {
    groups: [],
    topics: []
};

// Build the full content tree in memory
export function buildContentTree(groups: Group[], topics: Topic[], subtopics: Subtopic[]): ContentTree {
    topics.forEach(topic => {
        // Ensure each topic has a valid parentGroupId
        // if (!groups.some(g => g.id === topic.parentGroupId)) {
        //     console.warn(`Topic ${topic.id} has invalid parentGroupId: ${topic.parentGroupId}`);
        //     topic.parentGroupId = ''; // or handle as needed
        // }
        topic.subtopics = subtopics.filter(s => s.parentTopicId === topic.id);
    });
    // Attach topics to groups
    groups.forEach(group => {
        group.topics = topics.filter(t => t.parentGroupId === group.id);
    });

    _contentTree = { groups, topics, subtopics };
    return _contentTree;
}

// export function setContentTree(tree: ContentTree) {
//     _contentTree = tree;
// }

function getContentTree(): ContentTree | null {
    return _contentTree;
}

export function getTreeForSubtopic(subtopicId: string) {
    let contentTree = _contentTree;
    if (!contentTree || !contentTree.groups || !contentTree.topics || !contentTree.subtopics) return null;
    const subtopic = contentTree.subtopics.find(s => s.id === subtopicId);
    if (!subtopic) return null;
    const topic = contentTree.topics.find(t => t.id === subtopic.parentTopicId);
    if (!topic) return null;
    const group = contentTree.groups.find(g => g.topics.some(t => t.id === topic.id));
    if (!group) return null;
    return {
        group: {
            id: group.id,
            title: group.title,
            topic: {
                id: topic.id,
                title: topic.title,
                subtopic: {
                    id: subtopic.id,
                    title: subtopic.title
                }
            }
        }
    };
}

export function getTreeJson() {
    return JSON.stringify(_contentTree);
}

export function dumpContentTree() {
    console.log(`Building content tree with ${_contentTree.groups.length} groups, ${_contentTree.topics.length} topics, ${_contentTree.subtopics.length} subtopics`);
    let tree = "";
    _contentTree.groups.forEach(group => {
        //tree = tree + `Group: ${group.title}`;
        group.topics.forEach(topic => {
            //tree = tree + ` => Topic: ${topic.title}`;
            topic.subtopics.forEach(subtopic => {
                tree = tree + `Group: ${group.title}(${group.orderNumber}) => Topic: ${topic.title}(${topic.orderNumber}) => Subtopic: ${subtopic.title}(${subtopic.orderNumber}) \n`;
            });
        })
    });
    console.log(tree);

    orphans.subtopics = _contentTree.subtopics.filter(s => _contentTree.topics.filter(t => t.id === s.parentTopicId).length === 0);
    orphans.topics = _contentTree.topics.filter(t => _contentTree.groups.filter(g => g.id === t.parentGroupId).length === 0);

    noChildren.groups = _contentTree.groups.filter(g => g.topics.length === 0);
    noChildren.topics = _contentTree.topics.filter(t => t.subtopics.length === 0);

    console.log("Orphans:", orphans);
    console.log("No children:", noChildren);
}

function getGlobalTree() {
    return globalTree;
}

// Make dumpContentTree available globally for debugging
if (typeof window !== 'undefined') {
    (window as any).dumpContentTree = dumpContentTree;
    (window as any).getTreeJson = getTreeJson;
    (window as any).getGlobalTree = getGlobalTree;

}

const _StaticContentTree = {
    "groups": [
        {
            "id": "6830d6394482600002ac28f8",
            "title": "Understanding the Diagnosis",
            "orderNumber": 1,
            "topics": [
            ]
        },
        {
            "id": "6830d62b4482600002ac28f7",
            "title": "Care Team Coordination",
            "orderNumber": 2,
            "topics": [
            ]
        },
        {
            "id": "6830d6494482600002ac28f9",
            "title": "Planning for the Future",
            "orderNumber": 3,
            "topics": [
            ]
        },
        {
            "id": "683606dfece9e90002260df8",
            "title": "Community Resources",
            "orderNumber": 4,
            "topics": [
            ]
        },
        {
            "id": "683606a2ece9e90002260df5",
            "title": "Symptoms & Safety",
            "orderNumber": 5,
            "topics": [
            ]
        },
        {
            "id": "683606b4ece9e90002260df6",
            "title": "Medication Options",
            "orderNumber": 6,
            "topics": [
            ]
        },
        {
            "id": "683606ccece9e90002260df7",
            "title": "Living Well",
            "orderNumber": 7,
            "topics": [
            ]
        },
        {
            "id": "683606f7ece9e90002260df9",
            "title": "Caregiver Support",
            "orderNumber": 8,
            "topics": [
            ]
        }
    ],
    "topics": [
        {
            "id": "68421dfeb4016b00027632e5",
            "title": "Alzheimer’s Disease Overview",
            "orderNumber": 11,
            "parentGroupId": "6830d6394482600002ac28f8",
            "subtopics": [
            ]
        },
        {
            "id": "6842495fcc4128000241c4cf",
            "title": "Alzheimer’s Disease Diagnosis",
            "orderNumber": 12,
            "parentGroupId": "6830d6394482600002ac28f8",
            "subtopics": [
            ]
        },
        {
            "id": "6842498fcc4128000241c4d0",
            "title": "What Is Mixed Dementia",
            "orderNumber": 13,
            "parentGroupId": "6830d6394482600002ac28f8",
            "subtopics": [
            ]
        },
        {
            "id": "684249b9cc4128000241c4d1",
            "title": "Alzheimer’s Disease Stages ",
            "orderNumber": 14,
            "parentGroupId": "6830d6394482600002ac28f8",
            "subtopics": [
            ]
        },
        {
            "id": "68424a3acc4128000241c4d2",
            "title": "Managing Alzheimer’s Disease",
            "orderNumber": 15,
            "parentGroupId": "6830d6394482600002ac28f8",
            "subtopics": [
            ]
        },
        {
            "id": "68424ac7cc4128000241c4d3",
            "title": "Creating a Care Team",
            "orderNumber": 21,
            "parentGroupId": "6830d62b4482600002ac28f7",
            "subtopics": [
            ]
        },
        {
            "id": "683609b04b705400020fb989",
            "title": "Communicating with Someone Who Has Alzheimer's Disease",
            "orderNumber": 22,
            "parentGroupId": "6830d62b4482600002ac28f7",
            "subtopics": [
            ]
        },
        {
            "id": "683609a04b705400020fb988",
            "title": "Talking to Other People",
            "orderNumber": 23,
            "parentGroupId": "6830d62b4482600002ac28f7",
            "subtopics": [
            ]
        },
        {
            "id": "683609874b705400020fb987",
            "title": "Managing Family Dynamics",
            "orderNumber": 24,
            "parentGroupId": "6830d62b4482600002ac28f7",
            "subtopics": [
            ]
        },
        {
            "id": "6836091c4b705400020fb986",
            "title": "Doctor Visits",
            "orderNumber": 25,
            "parentGroupId": "6830d62b4482600002ac28f7",
            "subtopics": [
            ]
        },
        {
            "id": "6830d6714482600002ac28fa",
            "title": "Creating Meaningful Moments",
            "orderNumber": 26,
            "parentGroupId": "6830d62b4482600002ac28f7",
            "subtopics": [
            ]
        },
        {
            "id": "684254c237f6560002187170",
            "title": "Legal and Financial Planning",
            "orderNumber": 31,
            "parentGroupId": "6830d6494482600002ac28f9",
            "subtopics": [
            ]
        },
        {
            "id": "6842558637f6560002187171",
            "title": "Insurance Coverage",
            "orderNumber": 32,
            "parentGroupId": "6830d6494482600002ac28f9",
            "subtopics": [
            ]
        },
        {
            "id": "68507108f302960002ea1296",
            "title": "Dementia Care Facilities",
            "orderNumber": 33,
            "parentGroupId": "6830d6494482600002ac28f9",
            "subtopics": [
            ]
        },
        {
            "id": "68425600e874db00021d0a92",
            "title": "Support Services",
            "orderNumber": 42,
            "parentGroupId": "683606dfece9e90002260df8",
            "subtopics": [
            ]
        },
        {
            "id": "68425617e874db00021d0a93",
            "title": "Support Groups",
            "orderNumber": 43,
            "parentGroupId": "683606dfece9e90002260df8",
            "subtopics": [
            ]
        },
        {
            "id": "684256d1e874db00021d0a94",
            "title": "Depression",
            "orderNumber": 51,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "685071fef302960002ea1297",
            "title": "Anxiety",
            "orderNumber": 52,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "685072297ae7a40002a31e0f",
            "title": "Initiative, Attention, and Logic ",
            "orderNumber": 53,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "685072457ae7a40002a31e10",
            "title": "Home Safety Modifications",
            "orderNumber": 54,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "685072587ae7a40002a31e11",
            "title": "Driving Issues",
            "orderNumber": 55,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "6850726b7ae7a40002a31e12",
            "title": "Sleep",
            "orderNumber": 56,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "6850727e7ae7a40002a31e13",
            "title": "Balance and Falls",
            "orderNumber": 57,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "6850729e7ae7a40002a31e14",
            "title": "Behavioral Changes",
            "orderNumber": 58,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "685072c27ae7a40002a31e15",
            "title": "Sexuality",
            "orderNumber": 59,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "685072d47ae7a40002a31e16",
            "title": "Personality Changes",
            "orderNumber": 60,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "684ba85c8ee80d0002039abc",
            "title": "Managing Medications",
            "orderNumber": 61,
            "parentGroupId": "683606b4ece9e90002260df6",
            "subtopics": [
            ]
        },
        {
            "id": "685072f07ae7a40002a31e17",
            "title": "Alcohol and Smoking",
            "orderNumber": 61,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "68520c69a8d02e0002610f67",
            "title": "Medications and Alzheimer’s Disease",
            "orderNumber": 62,
            "parentGroupId": "683606b4ece9e90002260df6",
            "subtopics": [
            ]
        },
        {
            "id": "6850730d7ae7a40002a31e18",
            "title": "Appetite and Eating",
            "orderNumber": 62,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "685073d97ae7a40002a31e1e",
            "title": "Medication Concerns",
            "orderNumber": 63,
            "parentGroupId": "683606b4ece9e90002260df6",
            "subtopics": [
            ]
        },
        {
            "id": "6850732d7ae7a40002a31e19",
            "title": "Personal Care Challenges",
            "orderNumber": 63,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "685073437ae7a40002a31e1a",
            "title": "Delusions and Hallucinations",
            "orderNumber": 64,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "6850735636557c000201892e",
            "title": "Disorientation",
            "orderNumber": 65,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "6850736736557c000201892f",
            "title": "Delirium ",
            "orderNumber": 66,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "6850738a7ae7a40002a31e1b",
            "title": "Agitation and Sundowning",
            "orderNumber": 67,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "685073997ae7a40002a31e1c",
            "title": "Incontinence",
            "orderNumber": 68,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "685073ae7ae7a40002a31e1d",
            "title": "Professional Care Options",
            "orderNumber": 69,
            "parentGroupId": "683606a2ece9e90002260df5",
            "subtopics": [
            ]
        },
        {
            "id": "684ba56f97d94e00022a67fc",
            "title": "Activities and Alzheimer's Disease",
            "orderNumber": 71,
            "parentGroupId": "683606ccece9e90002260df7",
            "subtopics": [
            ]
        },
        {
            "id": "684ba60197d94e00022a67fd",
            "title": "Dementia-Friendly Activities ",
            "orderNumber": 72,
            "parentGroupId": "683606ccece9e90002260df7",
            "subtopics": [
            ]
        },
        {
            "id": "68363a35b7ad0d000275d13d",
            "title": "Encouraging Exercise",
            "orderNumber": 73,
            "parentGroupId": "683606ccece9e90002260df7",
            "subtopics": [
            ]
        },
        {
            "id": "6850741b36557c0002018930",
            "title": "Dietary Considerations",
            "orderNumber": 74,
            "parentGroupId": "683606ccece9e90002260df7",
            "subtopics": [
            ]
        },
        {
            "id": "6850742d36557c0002018931",
            "title": "Helpful Assistive Devices",
            "orderNumber": 75,
            "parentGroupId": "683606ccece9e90002260df7",
            "subtopics": [
            ]
        },
        {
            "id": "68360a4a4b705400020fb990",
            "title": "Common Caregiver Stressors",
            "orderNumber": 81,
            "parentGroupId": "683606f7ece9e90002260df9",
            "subtopics": [
            ]
        },
        {
            "id": "68360a394b705400020fb98f",
            "title": "Balancing Work and Caregiving",
            "orderNumber": 82,
            "parentGroupId": "683606f7ece9e90002260df9",
            "subtopics": [
            ]
        },
        {
            "id": "683609f94b705400020fb98b",
            "title": "Coping with Stress",
            "orderNumber": 83,
            "parentGroupId": "683606f7ece9e90002260df9",
            "subtopics": [
            ]
        },
        {
            "id": "68360a264b705400020fb98e",
            "title": "Technology and Caregiving",
            "orderNumber": 84,
            "parentGroupId": "683606f7ece9e90002260df9",
            "subtopics": [
            ]
        },
        {
            "id": "68360a074b705400020fb98c",
            "title": "Signs of Burnout",
            "orderNumber": 85,
            "parentGroupId": "683606f7ece9e90002260df9",
            "subtopics": [
            ]
        },
        {
            "id": "68360a154b705400020fb98d",
            "title": "Maintaining Your Own Health",
            "orderNumber": 86,
            "parentGroupId": "683606f7ece9e90002260df9",
            "subtopics": [
            ]
        }
    ],
    "subtopics": [
        {
            "id": "68421ee6b4016b00027632e7",
            "title": "Alzheimer’s Disease Definition",
            "orderNumber": 111,
            "parentTopicId": "68421dfeb4016b00027632e5",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "684239edf7ffe20002aac0a4",
            "title": "Alzheimer’s Disease Symptoms",
            "orderNumber": 112,
            "parentTopicId": "68421dfeb4016b00027632e5",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "68423a8df7ffe20002aac0a5",
            "title": "Other Types of Dementia",
            "orderNumber": 113,
            "parentTopicId": "68421dfeb4016b00027632e5",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "68423c15f7ffe20002aac0a6",
            "title": "Stages of Alzheimer’s Disease",
            "orderNumber": 114,
            "parentTopicId": "68421dfeb4016b00027632e5",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "68423c83f7ffe20002aac0a7",
            "title": "Risk Factors for Alzheimer’s Disease",
            "orderNumber": 115,
            "parentTopicId": "68421dfeb4016b00027632e5",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "68423cf6f7ffe20002aac0a8",
            "title": "Alzheimer’s Disease and Genetics",
            "orderNumber": 116,
            "parentTopicId": "68421dfeb4016b00027632e5",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "68424604988296000270b8b0",
            "title": "Managing Alzheimer’s Disease as a Caregiver",
            "orderNumber": 117,
            "parentTopicId": "68421dfeb4016b00027632e5",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "68424641988296000270b8b1",
            "title": "Helpful Alzheimer’s Disease Terms",
            "orderNumber": 118,
            "parentTopicId": "68421dfeb4016b00027632e5",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a7162e5fb700028a8949",
            "title": "Importance of Alzheimer's Disease Diagnosis",
            "orderNumber": 121,
            "parentTopicId": "6842495fcc4128000241c4cf",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a74e70974f0002d02514",
            "title": "Diagnosing Alzheimer's Disease",
            "orderNumber": 122,
            "parentTopicId": "6842495fcc4128000241c4cf",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a76c70974f0002d02515",
            "title": "Standard Alzheimer's Disease Blood Tests",
            "orderNumber": 123,
            "parentTopicId": "6842495fcc4128000241c4cf",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a7a770974f0002d02516",
            "title": "Alzheimer's Disease Imaging Tests (CT and MRI)",
            "orderNumber": 124,
            "parentTopicId": "6842495fcc4128000241c4cf",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a7f070974f0002d02517",
            "title": "Importance of a Neurologist in Alzheimer's Disease ",
            "orderNumber": 125,
            "parentTopicId": "6842495fcc4128000241c4cf",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a80a70974f0002d02518",
            "title": "Role of a Second Opinion in Alzheimer's Disease",
            "orderNumber": 126,
            "parentTopicId": "6842495fcc4128000241c4cf",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a82470974f0002d02519",
            "title": "Role of Neuropsychological Testing in Alzheimer's Disease ",
            "orderNumber": 127,
            "parentTopicId": "6842495fcc4128000241c4cf",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a84070974f0002d0251a",
            "title": "Role of Patient and Family Genetic Testing for Alzheimer's Disease",
            "orderNumber": 128,
            "parentTopicId": "6842495fcc4128000241c4cf",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a86970974f0002d0251b",
            "title": "Coping with an Alzheimer's Disease Diagnosis",
            "orderNumber": 129,
            "parentTopicId": "6842495fcc4128000241c4cf",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a96d70974f0002d0251c",
            "title": "Definition of Mixed Dementia",
            "orderNumber": 131,
            "parentTopicId": "6842498fcc4128000241c4d0",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a99470974f0002d0251d",
            "title": "Benefits to Knowing about Mixed Dementia ",
            "orderNumber": 132,
            "parentTopicId": "6842498fcc4128000241c4d0",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850a9e970974f0002d0251e",
            "title": "Progression of Alzheimer's Disease",
            "orderNumber": 141,
            "parentTopicId": "684249b9cc4128000241c4d1",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850aa7e70974f0002d0251f",
            "title": "MCI",
            "orderNumber": 142,
            "parentTopicId": "684249b9cc4128000241c4d1",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850ab0470974f0002d02520",
            "title": "Early-Stage Alzheimer’s (Mild)",
            "orderNumber": 143,
            "parentTopicId": "684249b9cc4128000241c4d1",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850ab5970974f0002d02521",
            "title": "Middle-Stage Alzheimer's (Moderate)",
            "orderNumber": 144,
            "parentTopicId": "684249b9cc4128000241c4d1",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850abaa70974f0002d02522",
            "title": "Late-Stage Alzheimer's (Severe)",
            "orderNumber": 145,
            "parentTopicId": "684249b9cc4128000241c4d1",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850abc470974f0002d02523",
            "title": "Consistency of Alzheimer's Disease Progression",
            "orderNumber": 146,
            "parentTopicId": "684249b9cc4128000241c4d1",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850ac0b70974f0002d02524",
            "title": "Managing the Symptoms of Alzheimer's Disease",
            "orderNumber": 151,
            "parentTopicId": "68424a3acc4128000241c4d2",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850ac3770974f0002d02525",
            "title": "Slowing the Progression of Alzheimer's Disease",
            "orderNumber": 152,
            "parentTopicId": "68424a3acc4128000241c4d2",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850ac5670974f0002d02526",
            "title": "Making Home Safe and Supportive ",
            "orderNumber": 153,
            "parentTopicId": "68424a3acc4128000241c4d2",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850ac9470974f0002d02527",
            "title": "Considering Alzheimer's Disease Clinical Trials",
            "orderNumber": 154,
            "parentTopicId": "68424a3acc4128000241c4d2",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850acb570974f0002d02528",
            "title": "Frequency of Primary Care Physician (PCP) Visits",
            "orderNumber": 155,
            "parentTopicId": "68424a3acc4128000241c4d2",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850acd370974f0002d02529",
            "title": "Other Helpful Care Professionals",
            "orderNumber": 156,
            "parentTopicId": "68424a3acc4128000241c4d2",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850acf670974f0002d0252a",
            "title": "Role of an Academic Dementia Care Center",
            "orderNumber": 157,
            "parentTopicId": "68424a3acc4128000241c4d2",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "6850ad16d3995b0002e2a316",
            "title": "Considering Hospice and Timing",
            "orderNumber": 158,
            "parentTopicId": "68424a3acc4128000241c4d2",
            "parentGroupId": "6830d6394482600002ac28f8"
        },
        {
            "id": "684269b70ce7110002286426",
            "title": "Importance of a Care Team",
            "orderNumber": 211,
            "parentTopicId": "68424ac7cc4128000241c4d3",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "68426a5a0ce7110002286427",
            "title": "Building a Care Team",
            "orderNumber": 212,
            "parentTopicId": "68424ac7cc4128000241c4d3",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "68426acca489650002fa6b9b",
            "title": "Care Team Coordination",
            "orderNumber": 213,
            "parentTopicId": "68424ac7cc4128000241c4d3",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850ae32d3995b0002e2a317",
            "title": "Understanding Communication Changes",
            "orderNumber": 221,
            "parentTopicId": "683609b04b705400020fb989",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850ae50d3995b0002e2a318",
            "title": "Adapting Your Communication Style",
            "orderNumber": 222,
            "parentTopicId": "683609b04b705400020fb989",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850ae92d3995b0002e2a319",
            "title": "Using Visual Aids and Gestures",
            "orderNumber": 223,
            "parentTopicId": "683609b04b705400020fb989",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850aecbd3995b0002e2a31a",
            "title": "Encouraging Interaction",
            "orderNumber": 224,
            "parentTopicId": "683609b04b705400020fb989",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850aef1d3995b0002e2a31b",
            "title": "Overcoming Forgetfulness and Repetition",
            "orderNumber": 225,
            "parentTopicId": "683609b04b705400020fb989",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850af23d3995b0002e2a31c",
            "title": "Accepting Their Reality",
            "orderNumber": 226,
            "parentTopicId": "683609b04b705400020fb989",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850af7ad3995b0002e2a31d",
            "title": "General Tips",
            "orderNumber": 231,
            "parentTopicId": "683609a04b705400020fb988",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850afc4d3995b0002e2a31e",
            "title": "Talking to Family Members",
            "orderNumber": 232,
            "parentTopicId": "683609a04b705400020fb988",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b023d3995b0002e2a31f",
            "title": "Talking to Children",
            "orderNumber": 233,
            "parentTopicId": "683609a04b705400020fb988",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b04cd3995b0002e2a320",
            "title": "Talking to Friends ",
            "orderNumber": 234,
            "parentTopicId": "683609a04b705400020fb988",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b06dd3995b0002e2a321",
            "title": "Talking with the Community",
            "orderNumber": 235,
            "parentTopicId": "683609a04b705400020fb988",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b083d3995b0002e2a322",
            "title": "Talking to the Patient",
            "orderNumber": 236,
            "parentTopicId": "683609a04b705400020fb988",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b0bed3995b0002e2a323",
            "title": "General Tips",
            "orderNumber": 241,
            "parentTopicId": "683609874b705400020fb987",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b0e5d3995b0002e2a324",
            "title": "Open Communication",
            "orderNumber": 242,
            "parentTopicId": "683609874b705400020fb987",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b106d3995b0002e2a325",
            "title": "Division of Responsibilities",
            "orderNumber": 243,
            "parentTopicId": "683609874b705400020fb987",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b128d3995b0002e2a326",
            "title": "Caregiver Support",
            "orderNumber": 244,
            "parentTopicId": "683609874b705400020fb987",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b18ad3995b0002e2a327",
            "title": "Preparing for a Doctor’s Visit",
            "orderNumber": 251,
            "parentTopicId": "6836091c4b705400020fb986",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b1afd3995b0002e2a328",
            "title": "Listing Key Concerns",
            "orderNumber": 252,
            "parentTopicId": "6836091c4b705400020fb986",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b1dbd3995b0002e2a329",
            "title": "Gathering Information",
            "orderNumber": 253,
            "parentTopicId": "6836091c4b705400020fb986",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b1fdd3995b0002e2a32a",
            "title": "Advocating Effectively",
            "orderNumber": 254,
            "parentTopicId": "6836091c4b705400020fb986",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b221d3995b0002e2a32b",
            "title": "Communicating with the Doctor Directly",
            "orderNumber": 255,
            "parentTopicId": "6836091c4b705400020fb986",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b23fd3995b0002e2a32c",
            "title": "Including the Person with Alzheimer’s Disease",
            "orderNumber": 256,
            "parentTopicId": "6836091c4b705400020fb986",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b2f3d3995b0002e2a32d",
            "title": "General Tips",
            "orderNumber": 261,
            "parentTopicId": "6830d6714482600002ac28fa",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b322d3995b0002e2a32e",
            "title": "Engage in Favorite Pastimes",
            "orderNumber": 262,
            "parentTopicId": "6830d6714482600002ac28fa",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b346d3995b0002e2a32f",
            "title": "Reminisce Together",
            "orderNumber": 263,
            "parentTopicId": "6830d6714482600002ac28fa",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b366d3995b0002e2a330",
            "title": "Provide Purpose",
            "orderNumber": 264,
            "parentTopicId": "6830d6714482600002ac28fa",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b38dd3995b0002e2a331",
            "title": "Spend Time in Nature",
            "orderNumber": 265,
            "parentTopicId": "6830d6714482600002ac28fa",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b3aad3995b0002e2a332",
            "title": "Comfort and Connection",
            "orderNumber": 266,
            "parentTopicId": "6830d6714482600002ac28fa",
            "parentGroupId": "6830d62b4482600002ac28f7"
        },
        {
            "id": "6850b9e3277dfa00027368ed",
            "title": "Prepare for Planning",
            "orderNumber": 311,
            "parentTopicId": "684254c237f6560002187170",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850ba0d277dfa00027368ee",
            "title": "Financial Planning for Dementia",
            "orderNumber": 312,
            "parentTopicId": "684254c237f6560002187170",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850ba30277dfa00027368ef",
            "title": "Legal Planning for Dementia",
            "orderNumber": 313,
            "parentTopicId": "684254c237f6560002187170",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850ba62277dfa00027368f0",
            "title": "Establishing a POA",
            "orderNumber": 314,
            "parentTopicId": "684254c237f6560002187170",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850ba97277dfa00027368f1",
            "title": "Creating Advance Directives",
            "orderNumber": 315,
            "parentTopicId": "684254c237f6560002187170",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850bab9277dfa00027368f2",
            "title": "Planning for Long-Term Care",
            "orderNumber": 316,
            "parentTopicId": "684254c237f6560002187170",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850bb7b277dfa00027368f3",
            "title": "Insurance Coverage",
            "orderNumber": 321,
            "parentTopicId": "6842558637f6560002187171",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850bb9d277dfa00027368f4",
            "title": "Health Insurance",
            "orderNumber": 322,
            "parentTopicId": "6842558637f6560002187171",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850bbca277dfa00027368f5",
            "title": "Medicare",
            "orderNumber": 323,
            "parentTopicId": "6842558637f6560002187171",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850bbec277dfa00027368f6",
            "title": "Medicaid",
            "orderNumber": 324,
            "parentTopicId": "6842558637f6560002187171",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850bc0d277dfa00027368f7",
            "title": "Long-Term Care (LTC) and Life Insurance",
            "orderNumber": 325,
            "parentTopicId": "6842558637f6560002187171",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850bcce277dfa00027368f8",
            "title": "Dementia Care Facilities",
            "orderNumber": 331,
            "parentTopicId": "68507108f302960002ea1296",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850bd06277dfa00027368f9",
            "title": "Evaluating Dementia Care",
            "orderNumber": 332,
            "parentTopicId": "68507108f302960002ea1296",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850bd1c277dfa00027368fa",
            "title": "Questions to Ask When Visiting a Dementia Care Community",
            "orderNumber": 333,
            "parentTopicId": "68507108f302960002ea1296",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850bd44277dfa00027368fb",
            "title": "Planning Dementia Care",
            "orderNumber": 334,
            "parentTopicId": "68507108f302960002ea1296",
            "parentGroupId": "6830d6494482600002ac28f9"
        },
        {
            "id": "6850bea8277dfa00027368fc",
            "title": "Support Services",
            "orderNumber": 411,
            "parentTopicId": "68425600e874db00021d0a92",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850bec7277dfa00027368fd",
            "title": "Dementia and Caregiving Education",
            "orderNumber": 412,
            "parentTopicId": "68425600e874db00021d0a92",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850bee2277dfa00027368fe",
            "title": "Recreation and Social Support",
            "orderNumber": 413,
            "parentTopicId": "68425600e874db00021d0a92",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850befc277dfa00027368ff",
            "title": "Caregiver Support Programs",
            "orderNumber": 414,
            "parentTopicId": "68425600e874db00021d0a92",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850bf2b68d8c5000210340f",
            "title": "Daily Needs Support",
            "orderNumber": 415,
            "parentTopicId": "68425600e874db00021d0a92",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850bf8368d8c50002103410",
            "title": "Respite Care Services",
            "orderNumber": 416,
            "parentTopicId": "68425600e874db00021d0a92",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850bfbb68d8c50002103411",
            "title": "Memory Care Communities",
            "orderNumber": 417,
            "parentTopicId": "68425600e874db00021d0a92",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850c05868d8c50002103412",
            "title": "Support Groups",
            "orderNumber": 421,
            "parentTopicId": "68425617e874db00021d0a93",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850c07668d8c50002103413",
            "title": "Support Resources for the Person with Dementia",
            "orderNumber": 422,
            "parentTopicId": "68425617e874db00021d0a93",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850c09768d8c50002103414",
            "title": "Support Resources for Care Partners",
            "orderNumber": 423,
            "parentTopicId": "68425617e874db00021d0a93",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850c0da68d8c50002103415",
            "title": "Local Community Centers",
            "orderNumber": 424,
            "parentTopicId": "68425617e874db00021d0a93",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850c0fd68d8c50002103416",
            "title": "Disease-Specific Support Groups",
            "orderNumber": 425,
            "parentTopicId": "68425617e874db00021d0a93",
            "parentGroupId": "683606dfece9e90002260df8"
        },
        {
            "id": "6850c186277dfa0002736900",
            "title": "Depression and Dementia",
            "orderNumber": 511,
            "parentTopicId": "684256d1e874db00021d0a94",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c1c4277dfa0002736901",
            "title": "Apathy versus Depression",
            "orderNumber": 512,
            "parentTopicId": "684256d1e874db00021d0a94",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c28c277dfa0002736902",
            "title": "Coping with Depression",
            "orderNumber": 513,
            "parentTopicId": "684256d1e874db00021d0a94",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c2ff277dfa0002736903",
            "title": "Medication for Depression",
            "orderNumber": 514,
            "parentTopicId": "684256d1e874db00021d0a94",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c347277dfa0002736904",
            "title": "Seeking Help",
            "orderNumber": 515,
            "parentTopicId": "684256d1e874db00021d0a94",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c3ea277dfa0002736905",
            "title": "Anxiety and Dementia",
            "orderNumber": 521,
            "parentTopicId": "685071fef302960002ea1297",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c40b277dfa0002736906",
            "title": "Shadowing",
            "orderNumber": 522,
            "parentTopicId": "685071fef302960002ea1297",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c486277dfa0002736907",
            "title": "Coping with Anxiety",
            "orderNumber": 523,
            "parentTopicId": "685071fef302960002ea1297",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c504277dfa0002736908",
            "title": "Medication for Anxiety",
            "orderNumber": 524,
            "parentTopicId": "685071fef302960002ea1297",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c54b277dfa0002736909",
            "title": "Seeking Help",
            "orderNumber": 525,
            "parentTopicId": "685071fef302960002ea1297",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c62d277dfa000273690a",
            "title": "Initiative, Attention, and Logic",
            "orderNumber": 531,
            "parentTopicId": "685072297ae7a40002a31e0f",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c671277dfa000273690b",
            "title": "Executive Functioning",
            "orderNumber": 532,
            "parentTopicId": "685072297ae7a40002a31e0f",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c6b5277dfa000273690c",
            "title": "Impact of Dementia on Executive Functioning",
            "orderNumber": 533,
            "parentTopicId": "685072297ae7a40002a31e0f",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c757277dfa000273690d",
            "title": "Adapting to Changes in Executive Functioning",
            "orderNumber": 534,
            "parentTopicId": "685072297ae7a40002a31e0f",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c7e8277dfa000273690e",
            "title": "Home Safety Modifications",
            "orderNumber": 541,
            "parentTopicId": "685072457ae7a40002a31e10",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c83c277dfa000273690f",
            "title": "Preventing Falls",
            "orderNumber": 542,
            "parentTopicId": "685072457ae7a40002a31e10",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c891277dfa0002736910",
            "title": "Wandering Prevention",
            "orderNumber": 543,
            "parentTopicId": "685072457ae7a40002a31e10",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c8da277dfa0002736911",
            "title": "Limit Access to Hazards",
            "orderNumber": 544,
            "parentTopicId": "685072457ae7a40002a31e10",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6850c930277dfa0002736912",
            "title": "Home Adaptations",
            "orderNumber": 545,
            "parentTopicId": "685072457ae7a40002a31e10",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f570e36bb50002b16da9",
            "title": "Driving Issues",
            "orderNumber": 551,
            "parentTopicId": "685072587ae7a40002a31e11",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f5dfe36bb50002b16daa",
            "title": "Dealing with Driving Concerns",
            "orderNumber": 552,
            "parentTopicId": "685072587ae7a40002a31e11",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f60fe36bb50002b16dab",
            "title": "Gathering Support",
            "orderNumber": 553,
            "parentTopicId": "685072587ae7a40002a31e11",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f637e36bb50002b16dac",
            "title": "Compassionate Conversations",
            "orderNumber": 554,
            "parentTopicId": "685072587ae7a40002a31e11",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f669e36bb50002b16dad",
            "title": "Example Conversation",
            "orderNumber": 555,
            "parentTopicId": "685072587ae7a40002a31e11",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f6afe36bb50002b16dae",
            "title": "Independence Alternatives",
            "orderNumber": 556,
            "parentTopicId": "685072587ae7a40002a31e11",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f70be36bb50002b16daf",
            "title": "Sleep",
            "orderNumber": 561,
            "parentTopicId": "6850726b7ae7a40002a31e12",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f736e36bb50002b16db0",
            "title": "Common Sleep Issues",
            "orderNumber": 562,
            "parentTopicId": "6850726b7ae7a40002a31e12",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f7a3e36bb50002b16db1",
            "title": "Sleeping Tips",
            "orderNumber": 563,
            "parentTopicId": "6850726b7ae7a40002a31e12",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f7d3e36bb50002b16db2",
            "title": "Sleeping Pills",
            "orderNumber": 564,
            "parentTopicId": "6850726b7ae7a40002a31e12",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f7f8e36bb50002b16db3",
            "title": "Alcohol and Sleep",
            "orderNumber": 565,
            "parentTopicId": "6850726b7ae7a40002a31e12",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f815e36bb50002b16db4",
            "title": "Fixing Sleep Is Challenging",
            "orderNumber": 566,
            "parentTopicId": "6850726b7ae7a40002a31e12",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f890e36bb50002b16db5",
            "title": "Balance and Falls",
            "orderNumber": 571,
            "parentTopicId": "6850727e7ae7a40002a31e13",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f8b6e36bb50002b16db6",
            "title": "Alzheimer’s Disease and Falls",
            "orderNumber": 572,
            "parentTopicId": "6850727e7ae7a40002a31e13",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851f906e36bb50002b16db7",
            "title": "Preventing Falls",
            "orderNumber": 573,
            "parentTopicId": "6850727e7ae7a40002a31e13",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851fb3ae36bb50002b16db8",
            "title": "Behavioral Changes",
            "orderNumber": 581,
            "parentTopicId": "6850729e7ae7a40002a31e14",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851fb8be36bb50002b16db9",
            "title": "Identifying Triggers",
            "orderNumber": 582,
            "parentTopicId": "6850729e7ae7a40002a31e14",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851fbe0e36bb50002b16dba",
            "title": "Creating a Calm Environment",
            "orderNumber": 583,
            "parentTopicId": "6850729e7ae7a40002a31e14",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851fc65e36bb50002b16dbb",
            "title": "Behavioral Management Strategies",
            "orderNumber": 584,
            "parentTopicId": "6850729e7ae7a40002a31e14",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851fccae36bb50002b16dbc",
            "title": "Professional Support and Therapies",
            "orderNumber": 585,
            "parentTopicId": "6850729e7ae7a40002a31e14",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851fd1ee36bb50002b16dbd",
            "title": "Sexuality",
            "orderNumber": 591,
            "parentTopicId": "685072c27ae7a40002a31e15",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851fd5be36bb50002b16dbe",
            "title": "Impact of Alzheimer's Disease on Sexual Behaviors",
            "orderNumber": 592,
            "parentTopicId": "685072c27ae7a40002a31e15",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851fd8fe36bb50002b16dbf",
            "title": "Managing Sexual Behaviors",
            "orderNumber": 593,
            "parentTopicId": "685072c27ae7a40002a31e15",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520df8e36bb50002b16ded",
            "title": "Understanding Medications",
            "orderNumber": 611,
            "parentTopicId": "684ba85c8ee80d0002039abc",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "68520e62a8d02e0002610f69",
            "title": "Setting Up a Medication Schedule",
            "orderNumber": 612,
            "parentTopicId": "684ba85c8ee80d0002039abc",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "68520e8da8d02e0002610f6a",
            "title": "Managing Medications",
            "orderNumber": 613,
            "parentTopicId": "684ba85c8ee80d0002039abc",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "68520f0ca8d02e0002610f6b",
            "title": "Monitoring for Side Effects",
            "orderNumber": 614,
            "parentTopicId": "684ba85c8ee80d0002039abc",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "68520f59a8d02e0002610f6c",
            "title": "Regular Reviews with Healthcare Providers",
            "orderNumber": 615,
            "parentTopicId": "684ba85c8ee80d0002039abc",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "68520fe3a8d02e0002610f6d",
            "title": "Medications Used to Treat Alzheimer’s Disease",
            "orderNumber": 621,
            "parentTopicId": "68520c69a8d02e0002610f67",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "68521049a8d02e0002610f6e",
            "title": "Medications to Slow the Progression of Alzheimer’s Disease",
            "orderNumber": 622,
            "parentTopicId": "68520c69a8d02e0002610f67",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "6852108be36bb50002b16dee",
            "title": "Medications Used to Treat the Symptoms Associated with Alzheimer’s Disease",
            "orderNumber": 623,
            "parentTopicId": "68520c69a8d02e0002610f67",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "685210b2e36bb50002b16def",
            "title": "Medications to Avoid for People with Alzheimer’s Disease",
            "orderNumber": 624,
            "parentTopicId": "68520c69a8d02e0002610f67",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "685211f06db1220002a14071",
            "title": "Impact of Over-the-Counter (OTC) Medication (e.g., Prevagen, vitamins, etc.)",
            "orderNumber": 631,
            "parentTopicId": "685073d97ae7a40002a31e1e",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "685213c16db1220002a14072",
            "title": "Side Effects of Prescribed Medication on Alzheimer’s Disease",
            "orderNumber": 632,
            "parentTopicId": "685073d97ae7a40002a31e1e",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "685214036db1220002a14073",
            "title": "Healthcare Provider Roles in Medication Review",
            "orderNumber": 633,
            "parentTopicId": "685073d97ae7a40002a31e1e",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "685214386db1220002a14074",
            "title": "The Role of Drug Interaction Engines on the Web",
            "orderNumber": 634,
            "parentTopicId": "685073d97ae7a40002a31e1e",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "685214786db1220002a14075",
            "title": "Pain Medications and Alzheimer’s Disease",
            "orderNumber": 635,
            "parentTopicId": "685073d97ae7a40002a31e1e",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "685214c06db1220002a14076",
            "title": "Alternative Approaches to Medications",
            "orderNumber": 636,
            "parentTopicId": "685073d97ae7a40002a31e1e",
            "parentGroupId": "683606b4ece9e90002260df6"
        },
        {
            "id": "6852b230902cac000227bf59",
            "title": "Activities and Alzheimer’s Disease",
            "orderNumber": 711,
            "parentTopicId": "684ba56f97d94e00022a67fc",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b260902cac000227bf5a",
            "title": "Considering Personal Preferences",
            "orderNumber": 712,
            "parentTopicId": "684ba56f97d94e00022a67fc",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b2c6902cac000227bf5b",
            "title": "Adapting Activities to a Changing Brain",
            "orderNumber": 713,
            "parentTopicId": "684ba56f97d94e00022a67fc",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b331902cac000227bf5c",
            "title": "Using Muscle Memory",
            "orderNumber": 714,
            "parentTopicId": "684ba56f97d94e00022a67fc",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b39c902cac000227bf5d",
            "title": "Traveling with Someone Who Has Alzheimer’s",
            "orderNumber": 715,
            "parentTopicId": "684ba56f97d94e00022a67fc",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b3e3902cac000227bf5e",
            "title": "Meaningful Activities",
            "orderNumber": 721,
            "parentTopicId": "684ba60197d94e00022a67fd",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b41b902cac000227bf5f",
            "title": "Arts and Crafts Projects",
            "orderNumber": 722,
            "parentTopicId": "684ba60197d94e00022a67fd",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b452902cac000227bf60",
            "title": "Puzzles and Games",
            "orderNumber": 723,
            "parentTopicId": "684ba60197d94e00022a67fd",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b48d902cac000227bf61",
            "title": "Physical Activities",
            "orderNumber": 724,
            "parentTopicId": "684ba60197d94e00022a67fd",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b4bd902cac000227bf62",
            "title": "Music and Dancing",
            "orderNumber": 725,
            "parentTopicId": "684ba60197d94e00022a67fd",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b4eb902cac000227bf63",
            "title": "Helping in the Kitchen",
            "orderNumber": 726,
            "parentTopicId": "684ba60197d94e00022a67fd",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b525902cac000227bf64",
            "title": "Daily Living Tasks",
            "orderNumber": 727,
            "parentTopicId": "684ba60197d94e00022a67fd",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b560902cac000227bf65",
            "title": "Enjoying Nature",
            "orderNumber": 728,
            "parentTopicId": "684ba60197d94e00022a67fd",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b59a902cac000227bf66",
            "title": "Safe Wandering",
            "orderNumber": 729,
            "parentTopicId": "684ba60197d94e00022a67fd",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b665902cac000227bf69",
            "title": "Encouraging Exercise",
            "orderNumber": 731,
            "parentTopicId": "68363a35b7ad0d000275d13d",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b69c902cac000227bf6a",
            "title": "Benefits of Exercise ",
            "orderNumber": 732,
            "parentTopicId": "68363a35b7ad0d000275d13d",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b6d5902cac000227bf6b",
            "title": "Appropriate Exercises ",
            "orderNumber": 733,
            "parentTopicId": "68363a35b7ad0d000275d13d",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b70a902cac000227bf6c",
            "title": "Amount of Exercise ",
            "orderNumber": 734,
            "parentTopicId": "68363a35b7ad0d000275d13d",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b740902cac000227bf6d",
            "title": "Implementation Tips",
            "orderNumber": 735,
            "parentTopicId": "68363a35b7ad0d000275d13d",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b79a902cac000227bf6e",
            "title": "Maintaining a Balanced Diet",
            "orderNumber": 741,
            "parentTopicId": "6850741b36557c0002018930",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b7ca902cac000227bf6f",
            "title": "Hydration",
            "orderNumber": 742,
            "parentTopicId": "6850741b36557c0002018930",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b7f7902cac000227bf70",
            "title": "Addressing Changes in Taste and Appetite",
            "orderNumber": 743,
            "parentTopicId": "6850741b36557c0002018930",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b829902cac000227bf71",
            "title": "Supporting Eating Skills",
            "orderNumber": 744,
            "parentTopicId": "6850741b36557c0002018930",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b85b902cac000227bf72",
            "title": "Adaptive Dining Tools",
            "orderNumber": 745,
            "parentTopicId": "6850741b36557c0002018930",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b888902cac000227bf73",
            "title": "Supplements and Special Diets",
            "orderNumber": 746,
            "parentTopicId": "6850741b36557c0002018930",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b8eb902cac000227bf74",
            "title": "Hearing Aids and Eyeglasses",
            "orderNumber": 751,
            "parentTopicId": "6850742d36557c0002018931",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b91b902cac000227bf75",
            "title": "Reminder Systems",
            "orderNumber": 752,
            "parentTopicId": "6850742d36557c0002018931",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b954902cac000227bf76",
            "title": "Phones and Remotes",
            "orderNumber": 753,
            "parentTopicId": "6850742d36557c0002018931",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b987902cac000227bf77",
            "title": "Canes and Walkers",
            "orderNumber": 754,
            "parentTopicId": "6850742d36557c0002018931",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b9ce902cac000227bf78",
            "title": "Wheelchairs",
            "orderNumber": 755,
            "parentTopicId": "6850742d36557c0002018931",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852ba16902cac000227bf79",
            "title": "Transfer Aids",
            "orderNumber": 756,
            "parentTopicId": "6850742d36557c0002018931",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852ba47902cac000227bf7a",
            "title": "Bed Rails",
            "orderNumber": 757,
            "parentTopicId": "6850742d36557c0002018931",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852ba9a902cac000227bf7b",
            "title": "Challenges for the Sandwich Generation",
            "orderNumber": 811,
            "parentTopicId": "68360a4a4b705400020fb990",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bad6902cac000227bf7c",
            "title": "Long-Distance Caregiving",
            "orderNumber": 812,
            "parentTopicId": "68360a4a4b705400020fb990",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bb06902cac000227bf7d",
            "title": "Changing Roles",
            "orderNumber": 813,
            "parentTopicId": "68360a4a4b705400020fb990",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bb6d902cac000227bf7e",
            "title": "Care Level Transitions",
            "orderNumber": 814,
            "parentTopicId": "68360a4a4b705400020fb990",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bb9c902cac000227bf7f",
            "title": "Lack of Support",
            "orderNumber": 815,
            "parentTopicId": "68360a4a4b705400020fb990",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bbff902cac000227bf80",
            "title": "Entering Late Stage",
            "orderNumber": 816,
            "parentTopicId": "68360a4a4b705400020fb990",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bc35902cac000227bf81",
            "title": "Grief",
            "orderNumber": 817,
            "parentTopicId": "68360a4a4b705400020fb990",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bdc5a0c971000222c200",
            "title": "Balancing Work and Caregiving ",
            "orderNumber": 821,
            "parentTopicId": "68360a394b705400020fb98f",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bdf2a0c971000222c201",
            "title": "Friends and Family",
            "orderNumber": 822,
            "parentTopicId": "68360a394b705400020fb98f",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852be2da0c971000222c202",
            "title": "Flexible Work Arrangements",
            "orderNumber": 823,
            "parentTopicId": "68360a394b705400020fb98f",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852be71a0c971000222c203",
            "title": "FMLA",
            "orderNumber": 824,
            "parentTopicId": "68360a394b705400020fb98f",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bea1a0c971000222c204",
            "title": "Utilizing Adult Day Care Services",
            "orderNumber": 825,
            "parentTopicId": "68360a394b705400020fb98f",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bed0a0c971000222c205",
            "title": "Seeking Support from Supervisors and Coworkers",
            "orderNumber": 826,
            "parentTopicId": "68360a394b705400020fb98f",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bef9a0c971000222c206",
            "title": "Prioritizing and Organizing Tasks",
            "orderNumber": 827,
            "parentTopicId": "68360a394b705400020fb98f",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bf4da0c971000222c207",
            "title": "Self-Care",
            "orderNumber": 831,
            "parentTopicId": "683609f94b705400020fb98b",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852bfa3a0c971000222c208",
            "title": "Life Balance",
            "orderNumber": 832,
            "parentTopicId": "683609f94b705400020fb98b",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c026a0c971000222c209",
            "title": "Re-evaluating Priorities",
            "orderNumber": 833,
            "parentTopicId": "683609f94b705400020fb98b",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c058a0c971000222c20a",
            "title": "Support Groups",
            "orderNumber": 834,
            "parentTopicId": "683609f94b705400020fb98b",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c08aa0c971000222c20b",
            "title": "Coping Techniques",
            "orderNumber": 835,
            "parentTopicId": "683609f94b705400020fb98b",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c0d3a0c971000222c20c",
            "title": "Counseling and Therapy",
            "orderNumber": 836,
            "parentTopicId": "683609f94b705400020fb98b",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c103a0c971000222c20d",
            "title": "Mindfulness and Relaxation Techniques",
            "orderNumber": 837,
            "parentTopicId": "683609f94b705400020fb98b",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c14afd8a2c00026e4118",
            "title": "Technology and Caregiving",
            "orderNumber": 841,
            "parentTopicId": "68360a264b705400020fb98e",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c183fd8a2c00026e4119",
            "title": "Safety and Monitoring Devices",
            "orderNumber": 842,
            "parentTopicId": "68360a264b705400020fb98e",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c20efd8a2c00026e411a",
            "title": "Video Calls",
            "orderNumber": 843,
            "parentTopicId": "68360a264b705400020fb98e",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c247fd8a2c00026e411b",
            "title": "Communication Aids",
            "orderNumber": 844,
            "parentTopicId": "68360a264b705400020fb98e",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c29ffd8a2c00026e411c",
            "title": "Cognitive Assistance and Engagement Apps and Devices",
            "orderNumber": 845,
            "parentTopicId": "68360a264b705400020fb98e",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c30ffd8a2c00026e411d",
            "title": "Signs of Burnout",
            "orderNumber": 851,
            "parentTopicId": "68360a074b705400020fb98c",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c34ffd8a2c00026e411e",
            "title": "Increased Irritability and Frustration",
            "orderNumber": 852,
            "parentTopicId": "68360a074b705400020fb98c",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c38bfd8a2c00026e411f",
            "title": "Physical and Emotional Exhaustion",
            "orderNumber": 853,
            "parentTopicId": "68360a074b705400020fb98c",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c3bcfd8a2c00026e4120",
            "title": "Withdrawal from Social Activities",
            "orderNumber": 854,
            "parentTopicId": "68360a074b705400020fb98c",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c44dfd8a2c00026e4121",
            "title": "Feelings of Sadness or Depression",
            "orderNumber": 855,
            "parentTopicId": "68360a074b705400020fb98c",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c48ffd8a2c00026e4122",
            "title": "Decreased Immunity and Increased Illness",
            "orderNumber": 856,
            "parentTopicId": "68360a074b705400020fb98c",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c4affd8a2c00026e4123",
            "title": "Maintaining Your Own Health",
            "orderNumber": 861,
            "parentTopicId": "68360a154b705400020fb98d",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c4d6fd8a2c00026e4124",
            "title": "Prioritize Self-Care",
            "orderNumber": 862,
            "parentTopicId": "68360a154b705400020fb98d",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c501fd8a2c00026e4125",
            "title": "Stay Physically Active",
            "orderNumber": 863,
            "parentTopicId": "68360a154b705400020fb98d",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c52efd8a2c00026e4126",
            "title": "Maintain a Healthy Diet",
            "orderNumber": 864,
            "parentTopicId": "68360a154b705400020fb98d",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c560fd8a2c00026e4127",
            "title": "Seek and Accept Support",
            "orderNumber": 865,
            "parentTopicId": "68360a154b705400020fb98d",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c589fd8a2c00026e4128",
            "title": "Realistic Expectations",
            "orderNumber": 866,
            "parentTopicId": "68360a154b705400020fb98d",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6852c5b1fd8a2c00026e4129",
            "title": "Go with the Flow",
            "orderNumber": 867,
            "parentTopicId": "68360a154b705400020fb98d",
            "parentGroupId": "683606f7ece9e90002260df9"
        },
        {
            "id": "6851fe98e36bb50002b16dc0",
            "title": "Personality Changes",
            "orderNumber": 5101,
            "parentTopicId": "685072d47ae7a40002a31e16",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851ff53e36bb50002b16dc1",
            "title": "Personality Changes with Alzheimer's Disease",
            "orderNumber": 5102,
            "parentTopicId": "685072d47ae7a40002a31e16",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851ff7be36bb50002b16dc2",
            "title": "Managing Personality Changes",
            "orderNumber": 5103,
            "parentTopicId": "685072d47ae7a40002a31e16",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6851ffaae36bb50002b16dc3",
            "title": "Lost Inhibitions",
            "orderNumber": 5104,
            "parentTopicId": "685072d47ae7a40002a31e16",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520001e36bb50002b16dc4",
            "title": "Drinking and Alzheimer’s Disease",
            "orderNumber": 5111,
            "parentTopicId": "685072f07ae7a40002a31e17",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520030e36bb50002b16dc5",
            "title": "Smoking and Alzheimer’s",
            "orderNumber": 5112,
            "parentTopicId": "685072f07ae7a40002a31e17",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6852006ae36bb50002b16dc6",
            "title": "Marijuana and Alzheimer’s",
            "orderNumber": 5113,
            "parentTopicId": "685072f07ae7a40002a31e17",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520098e36bb50002b16dc7",
            "title": "Conversation Tips",
            "orderNumber": 5114,
            "parentTopicId": "685072f07ae7a40002a31e17",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "685200ede36bb50002b16dc8",
            "title": "Appetite and Eating",
            "orderNumber": 5121,
            "parentTopicId": "6850730d7ae7a40002a31e18",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520112e36bb50002b16dc9",
            "title": "Alzheimer’s Disease and Eating",
            "orderNumber": 5122,
            "parentTopicId": "6850730d7ae7a40002a31e18",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6852028be36bb50002b16dcd",
            "title": "General Tips",
            "orderNumber": 5123,
            "parentTopicId": "6850732d7ae7a40002a31e19",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6852014be36bb50002b16dca",
            "title": "Weight Changes",
            "orderNumber": 5123,
            "parentTopicId": "6850730d7ae7a40002a31e18",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520190e36bb50002b16dcb",
            "title": "Encouraging Eating",
            "orderNumber": 5124,
            "parentTopicId": "6850730d7ae7a40002a31e18",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6852022fe36bb50002b16dcc",
            "title": "Personal Care Challenges",
            "orderNumber": 5131,
            "parentTopicId": "6850732d7ae7a40002a31e19",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "685202bfe36bb50002b16dce",
            "title": "Bathing ",
            "orderNumber": 5133,
            "parentTopicId": "6850732d7ae7a40002a31e19",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520308e36bb50002b16dcf",
            "title": "Grooming",
            "orderNumber": 5134,
            "parentTopicId": "6850732d7ae7a40002a31e19",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6852035ee36bb50002b16dd0",
            "title": "Dressing",
            "orderNumber": 5135,
            "parentTopicId": "6850732d7ae7a40002a31e19",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520386e36bb50002b16dd1",
            "title": "Oral Hygiene",
            "orderNumber": 5136,
            "parentTopicId": "6850732d7ae7a40002a31e19",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "685203bbe36bb50002b16dd2",
            "title": "Toileting",
            "orderNumber": 5137,
            "parentTopicId": "6850732d7ae7a40002a31e19",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6852042ee36bb50002b16dd3",
            "title": "Delusions and Hallucinations",
            "orderNumber": 5141,
            "parentTopicId": "685073437ae7a40002a31e1a",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520468e36bb50002b16dd4",
            "title": "Delusions and Confabulation",
            "orderNumber": 5142,
            "parentTopicId": "685073437ae7a40002a31e1a",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "685204a9e36bb50002b16dd5",
            "title": "Hallucinations and Illusions",
            "orderNumber": 5143,
            "parentTopicId": "685073437ae7a40002a31e1a",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6852054ce36bb50002b16dd6",
            "title": "Disorientation",
            "orderNumber": 5151,
            "parentTopicId": "6850735636557c000201892e",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520589e36bb50002b16dd7",
            "title": "Disorientation and Alzheimer’s Disease",
            "orderNumber": 5152,
            "parentTopicId": "6850735636557c000201892e",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "685205cde36bb50002b16dd8",
            "title": "Supporting a Disoriented Person",
            "orderNumber": 5153,
            "parentTopicId": "6850735636557c000201892e",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520634e36bb50002b16dd9",
            "title": "Delirium",
            "orderNumber": 5161,
            "parentTopicId": "6850736736557c000201892f",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "685206a2e36bb50002b16ddb",
            "title": "Managing Delirium",
            "orderNumber": 5163,
            "parentTopicId": "6850736736557c000201892f",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6852065ce36bb50002b16dda",
            "title": "Recognizing Treatable Issues",
            "orderNumber": 5163,
            "parentTopicId": "6850736736557c000201892f",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "685206fae36bb50002b16ddc",
            "title": "Preventing Delirium",
            "orderNumber": 5164,
            "parentTopicId": "6850736736557c000201892f",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520744e36bb50002b16ddd",
            "title": "Agitation in Alzheimer's Disease",
            "orderNumber": 5171,
            "parentTopicId": "6850738a7ae7a40002a31e1b",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520791e36bb50002b16dde",
            "title": "Managing Agitation",
            "orderNumber": 5172,
            "parentTopicId": "6850738a7ae7a40002a31e1b",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "685207f1e36bb50002b16ddf",
            "title": "Understanding Sundowning",
            "orderNumber": 5173,
            "parentTopicId": "6850738a7ae7a40002a31e1b",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6852083be36bb50002b16de0",
            "title": "Alzheimer’s Disease and Incontinence ",
            "orderNumber": 5181,
            "parentTopicId": "685073997ae7a40002a31e1c",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520862e36bb50002b16de1",
            "title": "Causes of Incontinence in Alzheimer’s Disease:",
            "orderNumber": 5182,
            "parentTopicId": "685073997ae7a40002a31e1c",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "685208a0e36bb50002b16de2",
            "title": "Evaluating Incontinence",
            "orderNumber": 5183,
            "parentTopicId": "685073997ae7a40002a31e1c",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6852090de36bb50002b16de3",
            "title": "Establishing a Routine ",
            "orderNumber": 5184,
            "parentTopicId": "685073997ae7a40002a31e1c",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520945e36bb50002b16de4",
            "title": "Environmental Adjustments",
            "orderNumber": 5185,
            "parentTopicId": "685073997ae7a40002a31e1c",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "685209afe36bb50002b16de5",
            "title": "Professional Care Options",
            "orderNumber": 5191,
            "parentTopicId": "685073ae7ae7a40002a31e1d",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "685209f9e36bb50002b16de6",
            "title": "In-Home Care Services",
            "orderNumber": 5192,
            "parentTopicId": "685073ae7ae7a40002a31e1d",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520a5fe36bb50002b16de7",
            "title": "Therapists ",
            "orderNumber": 5193,
            "parentTopicId": "685073ae7ae7a40002a31e1d",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520aa7e36bb50002b16de8",
            "title": "Adult Day Care Centers",
            "orderNumber": 5194,
            "parentTopicId": "685073ae7ae7a40002a31e1d",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520aebe36bb50002b16de9",
            "title": "Assisted Living Facilities (ALFs)",
            "orderNumber": 5195,
            "parentTopicId": "685073ae7ae7a40002a31e1d",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520b29e36bb50002b16dea",
            "title": "Memory Care Units",
            "orderNumber": 5196,
            "parentTopicId": "685073ae7ae7a40002a31e1d",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520b59e36bb50002b16deb",
            "title": "Nursing Homes",
            "orderNumber": 5197,
            "parentTopicId": "685073ae7ae7a40002a31e1d",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "68520b89e36bb50002b16dec",
            "title": "Hospice Care",
            "orderNumber": 5198,
            "parentTopicId": "685073ae7ae7a40002a31e1d",
            "parentGroupId": "683606a2ece9e90002260df5"
        },
        {
            "id": "6852b5d5902cac000227bf67",
            "title": "Adaptive Reading",
            "orderNumber": 7210,
            "parentTopicId": "684ba60197d94e00022a67fd",
            "parentGroupId": "683606ccece9e90002260df7"
        },
        {
            "id": "6852b613902cac000227bf68",
            "title": "Brain-Stimulating Activities",
            "orderNumber": 7211,
            "parentTopicId": "684ba60197d94e00022a67fd",
            "parentGroupId": "683606ccece9e90002260df7"
        }
    ]
}